import React, { useState } from 'react';
import {
    VStack,
    HStack,
    Text,
    Button,
    Alert,
    AlertIcon,
    SimpleGrid,
    Box,
    Spinner,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerHeader,
    DrawerBody,
    DrawerCloseButton,
    useDisclosure,
    Badge,
} from '@chakra-ui/react';
import { useIntl } from 'react-intl';
import { useGetUserCustomFields } from '../../query/resource-hooks/customFields';
import CustomFieldsForm from './CustomFieldsForm';
import CustomFieldDisplay from './CustomFieldDisplay';
import { EditIcon } from '@chakra-ui/icons';

// Section component props
export interface CustomFieldsSectionProps {
    userId: number;
    showEditButton?: boolean;
    maxFields?: number;
}

/**
 * Display component for user custom fields with optional edit functionality
 * Shows formatted field values and provides edit modal when permitted
 */
export const CustomFieldsSection: React.FC<CustomFieldsSectionProps> = ({ userId, showEditButton = false, maxFields }) => {
    const intl = useIntl();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [showAll, setShowAll] = useState(false);
    const formContainerRef = React.useRef(null);
    // Check if user has custom fields

    const { data: userFields, isLoading, isError, refetch } = useGetUserCustomFields(userId, !!userId);

    const handleFormSuccess = () => {
        refetch();
        onClose();
    };

    if (isLoading) {
        return (
            <Box p={4}>
                <HStack spacing={3}>
                    <Spinner size="sm" />
                    <Text fontWeight={'600'}>{intl.formatMessage({ id: 'customFields.loading' })}</Text>
                </HStack>
            </Box>
        );
    }

    if (isError) {
        return (
            <Alert status="error">
                <AlertIcon />
                <Text>{intl.formatMessage({ id: 'customFields.error' })}</Text>
            </Alert>
        );
    }

    // Show empty state if no fields and no edit permission
    if (!userFields?.data?.length && !showEditButton) {
        return null;
    }

    // Determine which fields to display
    const fieldsToShow = maxFields && !showAll ? userFields?.data.slice(0, maxFields) : userFields?.data;

    const hasMoreFields = maxFields && userFields && userFields?.data?.length > maxFields;

    return (
        <Box>
            {/* Section Header */}
            <HStack justify="space-between" mb={4}>
                <HStack spacing={3}>
                    <Text fontSize="lg" fontWeight="semibold">
                        {intl.formatMessage({ id: 'customFields.title' })}
                    </Text>

                    <Badge borderRadius={'lg'} variant="subtle">
                        {userFields?.data?.length || 0}
                    </Badge>
                </HStack>

                {showEditButton && (
                    <Button leftIcon={<EditIcon />} size="sm" variant="outline" onClick={onOpen}>
                        {intl.formatMessage({ id: 'customFields.edit' })}
                    </Button>
                )}
            </HStack>

            {/* Warnings */}
            {userFields && userFields?.warnings?.length && (
                <Alert status="warning" mb={4} size="sm">
                    <AlertIcon />
                    <VStack align="start" spacing={1}>
                        {userFields?.warnings?.map((warning, index) => (
                            <Text key={index} fontSize="sm">
                                {warning}
                            </Text>
                        ))}
                    </VStack>
                </Alert>
            )}

            {/* Field Display */}
            {userFields?.data?.length === 0 ? (
                <Alert status="info" borderRadius={'lg'}>
                    <AlertIcon />
                    <VStack align="start" spacing={1}>
                        <Text fontWeight="semibold">{intl.formatMessage({ id: 'customFields.noFields' })}</Text>
                        <Text fontSize="sm">{intl.formatMessage({ id: 'customFields.noFieldsDescription' })}</Text>
                    </VStack>
                </Alert>
            ) : (
                <VStack spacing={4} align="stretch">
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                        {fieldsToShow?.map((field) => <CustomFieldDisplay key={field.id} field={field} />)}
                    </SimpleGrid>

                    {/* Show All / Show Less Toggle */}
                    {hasMoreFields && (
                        <HStack justify="center" pt={2}>
                            <Button size="sm" variant="ghost" onClick={() => setShowAll(!showAll)}>
                                {showAll
                                    ? intl.formatMessage({ id: 'customFields.showLess' })
                                    : intl.formatMessage({ id: 'customFields.showAll' })}
                            </Button>
                        </HStack>
                    )}
                </VStack>
            )}

            {/* Edit Drawer */}
            <Drawer isOpen={isOpen} onClose={onClose} size="md">
                <DrawerOverlay />
                <DrawerContent m={{ sm: 0, md: 2 }} borderRadius={'xl'} ref={formContainerRef}>
                    <DrawerCloseButton />
                    <DrawerHeader>{intl.formatMessage({ id: 'customFields.edit' })}</DrawerHeader>
                    <DrawerBody>
                        <CustomFieldsForm
                            userId={userId}
                            onSuccess={handleFormSuccess}
                            onCancel={onClose}
                            containerRef={formContainerRef}
                        />
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </Box>
    );
};

export default CustomFieldsSection;
